import 'pages/e2/e2.prototype.css'
import 'components/public/page/index.css'
import cn from 'clsx'
import * as React from 'react'
import { connect } from 'react-redux'
import MediaQuery from 'react-responsive'
import type { GroupId } from 'types/group'
import type { NativeTag, Track } from 'types/track'
import type { EventSource, TrackLocation } from 'types/ui'
import type { TrackOptions } from '@disco/state-management/actions/MediaActions'
import * as MediaActions from '@disco/state-management/actions/MediaActions'
import GoogleAnalytics from '@disco/api/ga'
import mixpanel from '@disco/api/mixpanel'
import { AUDIO_FILE, DRM_FILE, VIDEO_FILE } from '@disco/constants/FileTypes'
import { LOADING, PLAYING } from '@disco/constants/MediaStatus'
import { MAIN } from '@disco/constants/Nesting'
import type { NestingVersionsContextType } from 'contexts/nesting-versions'
import { NestingVersionsContext } from 'contexts/nesting-versions'
import { ThemeContext } from 'contexts/theme'
import { getTrackPlaybackStatus } from '@disco/state-management/selectors/media'
import { getNestingLabel } from '@disco/state-management/selectors/nesting'
import { getTrack } from '@disco/state-management/selectors/tracks'
import { getFileTypeByExt } from '@disco/utils/file-utils'
import { attachGallery, getGalleryImages } from '@disco/utils/gallery'
import { getTrackSubheader, getViewPageLink } from '@disco/utils/tracks'
import Waveform from 'components/common/player/waveform'
import TrackArtwork from 'components/common/tracks/track-artwork'
import TrackCaption from 'components/public/track-caption'
import Flex from 'components/ui/flex'
import Lozenge from 'components/ui/lozenge'
import PlayTime from 'components/ui/play-time'
import getTrackStatus from 'components/utils/track-status'
import Playbar2 from 'pages/e2/playbar2'

const stopPropagation = (event: React.SyntheticEvent<any>) =>
  event.stopPropagation()

const mapStateToProps = (state, props) => {
  const { track } = props
  const trackInState = getTrack(state, {
    id: track.id,
  })
  return {
    ...getTrackPlaybackStatus(state, {
      trackId: props.track.id,
    }),
    track: trackInState || track,
  }
}

export type Props = {
  publicTrackItemRef: {
    current: null | HTMLDivElement
  }
  track: Track
  shareId: number | null | undefined
  playlistId?: number | null | undefined
  // deprecated in favour of playbackContext
  shortlistId?: string
  // deprecated in favour of playbackContext
  campaignId?: number | null | undefined
  // deprecated in favour of playbackContext
  recipientId?: number | null | undefined
  // deprecated in favour of playbackContext
  paneIndex: number
  showWaveform?: boolean
  location: TrackLocation
  // deprecated in favour of playbackContext
  mediaSource: EventSource
  // deprecated in favour of playbackContext
  groupId?: GroupId | null | undefined
  // deprecated in favour of playbackContext
  textSettings: 'default' | 'title_composer_description'
  actions?: React.ReactNode
  loadStatus: string
  playbackStatus: string
  pauseTrack: typeof MediaActions.pauseTrack
  playTrack: typeof MediaActions.playTrack
  onClick: () => void
  onTitleClick?: () => void
  onAlbumClick: (id: Track['album_id']) => void
  onArtistClick: (id: Track['artist_id']) => void
  onTagClick: (tag: NativeTag) => void
  onOpenPreview?: (...args: Array<any>) => any
  disabledLightgallery?: boolean
  playbackContext?: TrackOptions
  clientLibraryId?: number | null | undefined
  // deprecated in favour of playbackContext
  showNativeTags?: boolean
  trackVersionsButton?: React.ReactNode
  showcaseEnabled?: boolean
}

class PublicTrackItem extends React.Component<Props> {
  static contextType = NestingVersionsContext
  declare context: React.ContextType<typeof NestingVersionsContext>

  _handlePreviewClick = (e: React.SyntheticEvent<any>) => {
    e.stopPropagation()
    e.preventDefault()
    const { id, name, previews } = this.props.track
    const { location, onOpenPreview, disabledLightgallery, clientLibraryId } =
      this.props

    if (clientLibraryId) {
      if (onOpenPreview) onOpenPreview(name, previews)
      if (disabledLightgallery) return
    }

    const galleryImages = getGalleryImages(name, previews)
    attachGallery(`gallery-${id}`, galleryImages)
    const event = [
      'public-playlist',
      'public-track',
      'public-upload-page',
    ].includes(location)
      ? 'Preview non-media file on public page'
      : 'Preview non-media file in app'
    mixpanel.sendPureEvent(event)

    if (location === 'public-playlist') {
      GoogleAnalytics.sendEvent(
        'Other',
        'Preview non-media file on playlist page',
      )
    }

    if (location === 'public-track') {
      GoogleAnalytics.sendEvent('Other', 'Preview non-media file on track page')
    }
  }
  _handleControlsClick = (e: React.SyntheticEvent<any>) => {
    e.stopPropagation()
    e.preventDefault()
    const fileType = getFileTypeByExt(this.props.track.ext)
    if (fileType === DRM_FILE) return
    // don't handle clicks when status is loading to prevent exceptions throwing
    if (this.props.loadStatus === LOADING) return

    // TODO: handle location
    if (this.props.playbackStatus !== PLAYING) {
      this._playTrack()
    } else {
      this._pauseTrack()
    }
  }
  _handleWaveformClick = (e: React.SyntheticEvent<any>) => {
    e.stopPropagation()

    if (this.props.playbackStatus !== PLAYING) {
      // TODO: check if it's really needed
      this._playTrack()
    }
  }
  _playTrack = () => {
    const {
      track,
      playlistId,
      shortlistId,
      shareId,
      groupId,
      mediaSource,
      paneIndex,
      campaignId,
      recipientId,
      clientLibraryId,
    } = this.props
    let playTrackProps: TrackOptions = {
      playlistId,
      shortlistId,
      shareId,
      groupId,
      paneIndex,
      campaignId,
      recipientId,
    }

    if (clientLibraryId) {
      playTrackProps = {
        ...playTrackProps,
        clientLibraryId,
        ...this.props.playbackContext,
      }
    }

    this.props.playTrack(track.id, mediaSource, playTrackProps)
  }
  _pauseTrack = () => {
    const { track } = this.props
    this.props.pauseTrack(track.id)
  }
  _renderTrackTitle = () => {
    const { track, clientLibraryId, onTitleClick } = this.props
    const { line1 } = getTrackStatus(track)

    if (clientLibraryId) {
      return (
        <div
          className={cn('public-list-item-name overflow-ellipsis', {
            'public-list-item-link': onTitleClick,
          })}
          title={line1}
          translate="no"
          data-has-link={onTitleClick ? true : null}
          onClick={
            onTitleClick
              ? evt => {
                  evt.stopPropagation()
                  onTitleClick()
                }
              : undefined
          }
        >
          {line1}
        </div>
      )
    }

    return (
      <div
        className="public-list-item-name overflow-ellipsis"
        title={line1}
        translate="no"
      >
        {line1}
      </div>
    )
  }
  _renderTrackCaption = () => {
    const { track, textSettings, clientLibraryId } = this.props
    const line2 = getTrackSubheader(track, false, textSettings)

    if (clientLibraryId) {
      return (
        <Flex spacing="small">
          <div className="public-list-item-meta overflow-ellipsis">
            <TrackCaption
              settings={textSettings}
              track={track}
              onAlbumClick={this.props.onAlbumClick}
              onArtistClick={this.props.onArtistClick}
            />
          </div>
        </Flex>
      )
    }

    return (
      <div className="public-list-item-meta overflow-ellipsis" translate="no">
        {line2}
      </div>
    )
  }
  _renderNativeTags = () => {
    const { track, showNativeTags } = this.props
    return (
      showNativeTags && (
        <div className="public-list-item-details public-list-item-details--tags">
          <div className="public-list-item-meta overflow-ellipsis">
            {(track.native_tags || []).slice(0, 4).map((t, idx, { length }) => (
              <React.Fragment key={t.id}>
                <a
                  className="public-list-item-link"
                  onClick={evt => {
                    evt.stopPropagation()
                    this.props.onTagClick(t)
                  }}
                >
                  {t.name}
                </a>
                {idx < length - 1 ? <span>, </span> : null}
              </React.Fragment>
            ))}
          </div>
        </div>
      )
    )
  }
  _renderWaveform = () => {
    const { track, showWaveform } = this.props
    const { hasWaveform } = getTrackStatus(track)
    const fileType = getFileTypeByExt(track.ext)
    const isVideoFile = AUDIO_FILE === fileType
    const isAudioFile = VIDEO_FILE === fileType
    const isDocFile = !isVideoFile && !isAudioFile
    const nestingLabel = getNestingLabel(this.props.track)
    return (
      showWaveform &&
      hasWaveform &&
      !isDocFile && (
        <MediaQuery maxWidth={960}>
          {tablet => {
            if (tablet && nestingLabel && nestingLabel !== MAIN) {
              return <div className="public-list-item-waveform" />
            }

            return (
              <div className="public-list-item-waveform">
                {/* TODO: fix width on showcase playlist */}
                <div
                  className="public-list-item-progress"
                  onClick={this._handleWaveformClick}
                >
                  <ThemeContext.Consumer>
                    {themeContext => (
                      <Waveform
                        track={track}
                        barWidth={1.12}
                        barGap={2.18 - 1.12}
                        noAnimation
                        theme={themeContext?.theme ?? undefined}
                      />
                    )}
                  </ThemeContext.Consumer>
                </div>
                <div className="public-list-item-time">
                  <PlayTime time={track.duration} />
                </div>
              </div>
            )
          }}
        </MediaQuery>
      )
    )
  }
  _renderNestingLabel = () => {
    const { track, clientLibraryId, showcaseEnabled } = this.props
    const nestingLabel = getNestingLabel(track)
    const viewPageLink = getViewPageLink(track)
    if (viewPageLink) return null
    if (nestingLabel === MAIN) return null

    if (nestingLabel) {
      const labelColor = showcaseEnabled ? 'bold-gray' : 'gray'
      return (
        <div className="public-list-item-nesting-label">
          <Lozenge color={labelColor} text={nestingLabel} bold />
        </div>
      )
    }
  }
  _renderVersionsButton = () => {
    const { trackVersionsButton, showcaseEnabled, clientLibraryId } = this.props
    const renderButtonInLibraries = !this.context.nesting && clientLibraryId

    if (
      !trackVersionsButton ||
      (showcaseEnabled && this.context.nesting && !renderButtonInLibraries)
    ) {
      return null
    }

    return <div className="public-versions-button">{trackVersionsButton}</div>
  }

  render() {
    const {
      publicTrackItemRef,
      track,
      shareId,
      playlistId,
      paneIndex,
      campaignId,
      recipientId,
      groupId,
      actions,
      location,
      showWaveform,
    } = this.props
    const { bar, isTemp, isPlayable, isPreviewable } = getTrackStatus(track)
    let handleClick: ((e: React.SyntheticEvent<any>) => void) | undefined =
      undefined

    if (isPreviewable) {
      handleClick = this._handlePreviewClick
    } else if (isPlayable) {
      handleClick = this._handleControlsClick
    }

    const className = cn('public-list-item', {
      'public-list-item--disabled': location !== 'public-upload-page' && isTemp,
    })
    return (
      <div
        ref={publicTrackItemRef}
        className={className}
        onClick={this.props.onClick || handleClick}
      >
        <div className="public-list-item-content">
          <div
            className={cn('public-list-item-details-container', {
              'public-list-item-details-container--full-width': !showWaveform,
            })}
          >
            <div
              className={cn('public-list-item-artwork', {
                'public-list-item-artwork--hide': this.context.nesting,
              })}
              onClick={handleClick}
            >
              <TrackArtwork track={track} />
              {(isPreviewable || isPlayable) && (
                <Playbar2
                  src={track.internal_play_url}
                  ext={track.ext}
                  size={'small'}
                  track={track}
                  trackId={track.id}
                  paneIndex={paneIndex}
                  sourcePlaylistId={playlistId}
                  shareId={shareId}
                  campaignId={campaignId}
                  recipientId={recipientId}
                  groupId={groupId}
                  clientLibraryId={this.props.clientLibraryId}
                  playbackContext={this.props.playbackContext}
                  location={this.props.location}
                  mediaSource={this.props.mediaSource}
                />
              )}
            </div>
            <div className="public-list-item-details">
              {this._renderTrackTitle()}
              {this._renderTrackCaption()}
            </div>
            {this._renderVersionsButton()}
            {this._renderNativeTags()}
          </div>

          {this._renderWaveform()}
        </div>
        <div className="public-list-item-actions">
          {this._renderNestingLabel()}
          <div onClick={stopPropagation}>{actions}</div>
        </div>
        {bar}
      </div>
    )
  }
}

export default connect(mapStateToProps, MediaActions)(PublicTrackItem) as any
